import { useRadio, useRadioGroup } from "@react-aria/radio";
import { RadioGroupState, useRadioGroupState } from "@react-stately/radio";
import { AriaRadioGroupProps, AriaRadioProps } from "@react-types/radio";
import TextfieldLabel from "../TextField/TextfieldLabel";
import { createContext, useContext, useRef } from "react";
import { LabelAriaProps } from "@react-aria/label";
import { cn } from "../cn";
import { Typography } from "../Typography/Typography";
import { IconRadioActive, IconRadioInactive } from "../Icons";

export const RadioContext = createContext<RadioGroupState & {excludeFromTabOrder?: boolean}>(null!);

export const RadioGroup = ({excludeFromTabOrder, ...baseProps}: AriaRadioGroupProps & React.PropsWithChildren<{className?: string, excludeFromTabOrder?: boolean}>) => {
  const props = {
    ...baseProps,
    isInvalid: !!baseProps.errorMessage,
  };
  const state = {...useRadioGroupState(props), excludeFromTabOrder};
  const { radioGroupProps, labelProps, errorMessageProps } = useRadioGroup(props, state);
  
  return (
    <div {...radioGroupProps} className={props.className} tabIndex={excludeFromTabOrder ? -1 : undefined}>
      <TextfieldLabel labelProps={labelProps} required={props.isRequired}>
        {props.label}
      </TextfieldLabel>
      <RadioContext.Provider value={state}>
        {props.children}
        {props.errorMessage && (
          <Typography size='sm' className='text-error' {...errorMessageProps}>
            {props.errorMessage as any}
          </Typography> 
        )}
      </RadioContext.Provider>
    </div>
  );
};

export const RadioOption = (props: React.PropsWithChildren<{className?: string}> & AriaRadioProps & LabelAriaProps) => {
  const state = useContext(RadioContext);
  if (!state) {
    throw new Error('RadioOption must be used within a RadioContext');
  }
  
  const ref = useRef(null);
  const { inputProps, isSelected, isDisabled } = useRadio(props, state, ref);

  return (
    <label 
      className={cn(
        'flex items-center gap-1 cursor-pointer select-none',
        isDisabled && 'cursor-not-allowed opacity-50',
        props.className
      )}
    >
      <div className="relative flex items-center">
        <input
          {...inputProps}
          ref={ref}
          className="absolute w-0 h-0 opacity-0 peer"
          tabIndex={state.excludeFromTabOrder ? -1 : 0}
        />
        <div className={cn(
          'flex items-center justify-center text-text peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-400 rounded-full',
        )}>
          {isSelected ? <IconRadioActive className='w-[22px] h-[22px]'/> : <IconRadioInactive className='w-[22px] h-[22px]'/>}
        </div>
      </div>
      <Typography color='text' size='sm' weight={inputProps.checked ? 'semibold' : 'regular'}>{props.children}</Typography>
    </label>
  );
};